import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Callout, ConditionalWrapper, Tooltip, Page, Typography, Card, Column, Grid, GridItem, Button, Box, Row } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import LoadingIndicator from '~/components/LoadingIndicator';
import { openInNewTabProps, routes } from '~/constants';
import { useIntegrations } from '~/hooks/reactQuery/queries/useIntegrations';
import { useSalesTaxConfig } from '~/hooks/reactQuery/queries/useSalesTaxConfig/useSalesTaxConfig';
import { useHistoryLength } from '~/hooks/useNavigationBlocker';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { integrationModel } from '~/models';
import { AccountingAdvancedSettings } from '../AccountingAdvancedSettings/AccountingAdvancedSettings';
import styles from './AccountingSetupPage.module.scss';
import { getSubHeaderByIntegration } from './AccountingSetupPage.utils';
import { useAccountingSetupActions } from './hooks/useAccountingSetupActions';
import { useRedirectWhenAccountingUnavailable } from './hooks/useRedirectWhenAccountingUnavailable';
import { MapTaxRatesSetupRow } from './MapTaxRatesSetupRow';
export const GetAccountingSetupPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            AccountingSetupPageTID: {
                Page: (testIdPrefix)=>"".concat(testIdPrefix, "-setup-page"),
                DisconnectModal: (testIdPrefix)=>"".concat(testIdPrefix, "-disconnect-modal"),
                MultipleAccountsCallout: 'multiple-accounts-callout',
                MultipleAccountsCalloutButton: 'multiple-accounts-callout-button',
                MultipleAccountsCalloutText: 'multiple-accounts-callout-text',
                ConnectionSettings: 'connection-settings',
                ChooseAccounts: 'choose-accounts',
                MissingMappingErrorContacts: 'missing-account-mapping-error-contacts',
                MapContactsButton: 'map-contacts-button',
                MapContacts: 'map-contacts',
                MissingMappingErrorServices: 'missing-account-mapping-error-services',
                MapServicesButton: 'map-services-button',
                MapServices: 'map-services',
                AdvancedSettings: 'advanced-settings',
                DisconnectSection: (testIdPrefix)=>"disconnect-".concat(testIdPrefix),
                Disconnect: 'disconnect',
                Section: {
                    Root: (testId)=>"".concat(testId, "-section"),
                    Header: (testId)=>"".concat(testId, "-header"),
                    SubHeader: (testId)=>"".concat(testId, "-sub-header")
                }
            }
        }
    });
export function AccountingSetupPage(param) {
    let { accounting } = param;
    var _ref;
    const { t: accountingTexts } = useLocaleKeys('integrations.accounting');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { AccountingSetupPageTID } = useMemo(()=>GetAccountingSetupPageTID(), []);
    const { integrations } = useIntegrations({
        enabled: false
    });
    const accountingIntegration = integrationModel.accountingIntegration(integrations, accounting);
    const isPendingAccountingIntegrationSetup = integrationModel.isPendingAccountingIntegrationSetup(integrations, accounting);
    const isLinked = accountingIntegration && integrationModel.isLinked(accountingIntegration);
    const navigate = useNavigate();
    const historyLength = useHistoryLength();
    const { salesTaxSupported, isLoading: isSalesTaxConfigLoading } = useSalesTaxConfig();
    const { name: accountingName, fullName: accountingFullName, helpArticle, testIdPrefix } = getAccountingDetails(accounting);
    const title = accountingTexts.setupTitle({
        accountingFullName
    });
    const subheader = accountingTexts.manageConnection({
        companyName: (_ref = accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.companyName()) !== null && _ref !== void 0 ? _ref : ''
    });
    const { isRefreshingIntegrations } = useRedirectWhenAccountingUnavailable(accounting);
    const { disconnect, chooseAccounts, mapContacts, mapServices, isDisconnecting } = useAccountingSetupActions({
        accounting,
        disconnectModalTestId: AccountingSetupPageTID.DisconnectModal(testIdPrefix)
    });
    const subHeader = useMemo(()=>getSubHeaderByIntegration(accountingTexts, accountingIntegration !== null && accountingIntegration !== void 0 ? accountingIntegration : undefined), [
        accountingIntegration
    ]);
    const navigateBack = ()=>historyLength > 1 ? navigate(-1) : navigate(routes.availableApps);
    const renderMissingMappingTooltip = (testId, text)=>(children)=>/*#__PURE__*/ _jsx(Tooltip, {
                title: /*#__PURE__*/ _jsx(Box, {
                    testId: testId,
                    children: text
                }),
                children: children
            });
    return /*#__PURE__*/ _jsx(Page, {
        testId: AccountingSetupPageTID.Page(testIdPrefix),
        header: /*#__PURE__*/ _jsx(Row, {
            gap: 8,
            children: title
        }),
        subheader: /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            children: [
                subheader,
                /*#__PURE__*/ _jsx(Typography, _object_spread_props(_object_spread({
                    as: "a",
                    href: helpArticle.setup
                }, openInNewTabProps), {
                    children: commonButtons.learnMore()
                }))
            ]
        }),
        loading: isRefreshingIntegrations,
        onClickBack: navigateBack,
        children: isSalesTaxConfigLoading ? /*#__PURE__*/ _jsx(LoadingIndicator, {}) : /*#__PURE__*/ _jsxs(Column, {
            gap: 12,
            children: [
                (accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.isMultipleAccountsConnected()) && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    testId: AccountingSetupPageTID.MultipleAccountsCallout(),
                    withAction: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'go_to_billing_settings'
                        },
                        testId: AccountingSetupPageTID.MultipleAccountsCalloutButton(),
                        skin: "primaryOutline",
                        onClick: ()=>navigate(routes.businessDetails),
                        children: commonButtons.goToBillingSettings()
                    }),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        testId: AccountingSetupPageTID.MultipleAccountsCalloutText(),
                        children: accountingTexts.multipleAccountsCallout({
                            accountingFullName
                        })
                    })
                }),
                /*#__PURE__*/ _jsxs(Section, {
                    testId: AccountingSetupPageTID.ConnectionSettings(),
                    header: accountingTexts.connectionSettingsHeader(),
                    subHeader: subHeader,
                    isError: !accountingIntegration || accountingIntegration.isErrored(),
                    children: [
                        /*#__PURE__*/ _jsx(GridItemLeft, {
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: accountingTexts.selectAccountsUsed({
                                    accountingName
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(GridItemRight, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'choose_accounting_accounts'
                                },
                                className: styles.button,
                                testId: AccountingSetupPageTID.ChooseAccounts(),
                                intent: Intent.AccountingMapAccounts,
                                onClick: chooseAccounts,
                                disabled: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.isErrored(),
                                children: commonButtons.chooseAccounts()
                            })
                        }),
                        isLinked && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(GridItemLeft, {
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: accountingTexts.mapContactsTo({
                                            accountingName
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItemRight, {
                                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                        condition: isPendingAccountingIntegrationSetup,
                                        wrapper: renderMissingMappingTooltip(AccountingSetupPageTID.MissingMappingErrorContacts(), accountingTexts.toMapContactsMapAccountsFirst()),
                                        children: /*#__PURE__*/ _jsx(Box, {
                                            as: "span",
                                            testId: AccountingSetupPageTID.MapContactsButton(),
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                bi: {
                                                    name: 'map_accounting_contacts'
                                                },
                                                className: styles.button,
                                                disabled: isPendingAccountingIntegrationSetup,
                                                testId: AccountingSetupPageTID.MapContacts(),
                                                intent: Intent.AccountingSetupMapContacts,
                                                onClick: mapContacts,
                                                children: commonButtons.mapContacts()
                                            })
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItemLeft, {
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: accountingTexts.mapServicesTo({
                                            accountingName
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItemRight, {
                                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                        condition: isPendingAccountingIntegrationSetup,
                                        wrapper: renderMissingMappingTooltip(AccountingSetupPageTID.MissingMappingErrorServices(), accountingTexts.toMapServicesMapAccountsFirst()),
                                        children: /*#__PURE__*/ _jsx(Box, {
                                            as: "span",
                                            testId: AccountingSetupPageTID.MapServicesButton(),
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                bi: {
                                                    name: 'map_accounting_services'
                                                },
                                                className: styles.button,
                                                disabled: isPendingAccountingIntegrationSetup,
                                                testId: AccountingSetupPageTID.MapServices(),
                                                intent: Intent.AccountingMapServices,
                                                onClick: mapServices,
                                                children: commonButtons.mapServices()
                                            })
                                        })
                                    })
                                }),
                                salesTaxSupported && /*#__PURE__*/ _jsx(MapTaxRatesSetupRow, {
                                    accounting: accounting
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Section, {
                    testId: AccountingSetupPageTID.AdvancedSettings(),
                    header: accountingTexts.advancedSettings(),
                    children: /*#__PURE__*/ _jsx(AccountingAdvancedSettings, {
                        accounting: accounting,
                        isLinked: isLinked || false
                    })
                }),
                /*#__PURE__*/ _jsxs(Section, {
                    testId: AccountingSetupPageTID.DisconnectSection(testIdPrefix),
                    children: [
                        /*#__PURE__*/ _jsx(GridItemLeft, {
                            children: /*#__PURE__*/ _jsx(Typography, {
                                skin: "error",
                                children: accountingTexts.disconnectFrom({
                                    accountingFullName
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(GridItemRight, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'disconnect_accounting'
                                },
                                className: styles.button,
                                skin: "danger",
                                testId: AccountingSetupPageTID.Disconnect(),
                                intent: Intent.AccountingDisconnect,
                                onClick: disconnect,
                                loading: isDisconnecting,
                                disabled: !!accountingIntegration && accountingIntegration.isErrored(),
                                children: commonButtons.disconnect()
                            })
                        })
                    ]
                })
            ]
        })
    });
}
const Section = (param)=>{
    let { testId, header, subHeader, isError, children } = param;
    const { AccountingSetupPageTID } = GetAccountingSetupPageTID();
    return /*#__PURE__*/ _jsxs(Card, {
        bi: {
            name: 'accounting_setup_section_card',
            data: {
                section: testId !== null && testId !== void 0 ? testId : ''
            }
        },
        testId: AccountingSetupPageTID.Section.Root(testId),
        className: styles.section,
        children: [
            header && /*#__PURE__*/ _jsxs(Box, {
                className: styles.header,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        testId: AccountingSetupPageTID.Section.Header(testId),
                        weight: "normal",
                        variant: "h5",
                        children: header
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        skin: isError ? 'error' : 'secondary',
                        variant: "paragraph2",
                        testId: AccountingSetupPageTID.Section.SubHeader(testId),
                        children: subHeader
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Grid, {
                align: "center",
                rowGap: "small",
                children: children
            })
        ]
    });
};
const GridItemRight = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 2,
        justify: "center",
        children: children
    });
};
const GridItemLeft = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 10,
        children: children
    });
};
